import sensorTopicsData from './sensorTopics.json';

class SensorTopicsService {
  constructor() {
    this.topics = sensorTopicsData.dataTopics;
    this.topicsMap = new Map();
    this.sensorMap = new Map();
    
    // Create maps for faster lookups
    this.topics.forEach(topic => {
      this.topicsMap.set(topic.topic, topic);
      this.sensorMap.set(topic.sensor, topic);
    });
  }

  /**
   * Get all available sensor topics
   * @returns {Array} Array of sensor topic configurations
   */
  getAllTopics() {
    return [...this.topics];
  }

  /**
   * Get sensor configuration by sensor name
   * @param {string} sensorName - Name of the sensor
   * @returns {Object|null} Sensor configuration or null if not found
   */
  getSensorConfig(sensorName) {
    return this.sensorMap.get(sensorName) || null;
  }

  /**
   * Get sensor configuration by topic
   * @param {string} topic - Topic string
   * @returns {Object|null} Sensor configuration or null if not found
   */
  getConfigByTopic(topic) {
    return this.topicsMap.get(topic) || null;
  }

  /**
   * Get all sensor names
   * @returns {Array} Array of sensor names
   */
  getSensorNames() {
    return this.topics.map(topic => topic.sensor);
  }

  /**
   * Get all topic strings
   * @returns {Array} Array of topic strings
   */
  getTopicStrings() {
    return this.topics.map(topic => topic.topic);
  }

  /**
   * Get MQTT topic patterns for a unit
   * @param {string} unitId - Unit ID
   * @param {string} sensorName - Specific sensor name (optional)
   * @returns {Array} Array of MQTT topic patterns
   */
  getMqttTopics(unitId, sensorName = null) {
    const configs = sensorName ? 
      [this.getSensorConfig(sensorName)].filter(Boolean) : 
      this.topics;

    const mqttTopics = [];
    
    configs.forEach(config => {
      // Add multiple possible topic patterns
      mqttTopics.push(
        `${unitId}/+/data/${config.topic}`,
      );
    });

    return [...new Set(mqttTopics)]; // Remove duplicates
  }

  /**
   * Get sensors by data type
   * @param {string} dataType - Data type (e.g., 'uint16', 'int32')
   * @returns {Array} Array of sensor configurations
   */
  getSensorsByDataType(dataType) {
    return this.topics.filter(topic => topic.dataType === dataType);
  }

  /**
   * Get sensors by scaling factor
   * @param {number} scaling - Scaling factor
   * @returns {Array} Array of sensor configurations
   */
  getSensorsByScaling(scaling) {
    return this.topics.filter(topic => topic.scaling === scaling);
  }

  /**
   * Check if a sensor exists
   * @param {string} sensorName - Name of the sensor
   * @returns {boolean} True if sensor exists
   */
  hasSensor(sensorName) {
    return this.sensorMap.has(sensorName);
  }

  /**
   * Check if a topic exists
   * @param {string} topic - Topic string
   * @returns {boolean} True if topic exists
   */
  hasTopic(topic) {
    return this.topicsMap.has(topic);
  }

  /**
   * Get sensors with human-readable labels
   * @returns {Array} Array of objects with sensor info and labels
   */
  getSensorsWithLabels() {
    return this.topics.map(topic => ({
      ...topic,
      label: this.formatSensorLabel(topic.sensor),
      fullLabel: `${this.formatSensorLabel(topic.sensor)} (${topic.topic})`
    }));
  }

  /**
   * Format sensor name to human-readable label
   * @param {string} sensorName - Sensor name
   * @returns {string} Formatted label
   */
  formatSensorLabel(sensorName) {
    const labelMap = {
      'conductivity': 'Conductivity',
      'temperature': 'Temperature',
      'pressure': 'Pressure',
      'ph': 'pH Level',
      'turbidity': 'Turbidity',
      'flow': 'Flow Rate',
      'level': 'Water Level',
      'color3x': 'Color 3-Band X',
      'color3y': 'Color 3-Band Y',
      'color3z': 'Color 3-Band Z'
    };

    return labelMap[sensorName] || sensorName.charAt(0).toUpperCase() + sensorName.slice(1);
  }

  /**
   * Get grouped sensors (e.g., color3x, color3y, color3z as a group)
   * @returns {Object} Grouped sensors
   */
  getGroupedSensors() {
    const groups = {
      color3bands: [],
      individual: []
    };

    this.topics.forEach(topic => {
      if (topic.sensor.startsWith('color3')) {
        groups.color3bands.push(topic);
      } else {
        groups.individual.push(topic);
      }
    });

    return groups;
  }

  /**
   * Get data type for a specific sensor
   * @param {string} sensorName - Name of the sensor
   * @returns {string|null} Data type (e.g., 'uint16', 'int32') or null if sensor not found
   */
  getSensorDataType(sensorName) {
    const sensorConfig = this.getSensorConfig(sensorName);
    return sensorConfig ? sensorConfig.dataType : null;
  }

  /**
   * Get complete data format information for a sensor
   * @param {string} sensorName - Name of the sensor
   * @returns {Object|null} Object containing dataType, signed, and scaling or null if not found
   */
  getSensorDataFormat(sensorName) {
    const sensorConfig = this.getSensorConfig(sensorName);
    if (!sensorConfig) return null;
    
    return {
      dataType: sensorConfig.dataType,
      signed: sensorConfig.signed,
      scaling: sensorConfig.scaling
    };
  }

  /**
   * Get data format information for a topic
   * @param {string} topic - Topic string
   * @returns {Object|null} Object containing dataType, signed, and scaling or null if not found
   */
  getTopicDataFormat(topic) {
    const topicConfig = this.getConfigByTopic(topic);
    if (!topicConfig) return null;
    
    return {
      dataType: topicConfig.dataType,
      signed: topicConfig.signed,
      scaling: topicConfig.scaling
    };
  }

  /**
   * Validate sensor configuration
   * @param {Object} config - Sensor configuration
   * @returns {boolean} True if valid
   */
  validateSensorConfig(config) {
    const requiredFields = ['sensor', 'topic', 'dataType', 'signed', 'scaling'];
    const validDataTypes = ['uint16', 'int16', 'uint32', 'int32', 'float'];

    // Check required fields
    const hasAllFields = requiredFields.every(field => config.hasOwnProperty(field));
    if (!hasAllFields) return false;

    // Check data type
    if (!validDataTypes.includes(config.dataType)) return false;

    // Check scaling is a number
    if (typeof config.scaling !== 'number') return false;

    // Check signed is boolean
    if (typeof config.signed !== 'boolean') return false;

    return true;
  }
}

// Create singleton instance
const sensorTopicsService = new SensorTopicsService();

export default sensorTopicsService;

// Export the service class for testing
export { SensorTopicsService };